/**
 * The text of a constructor component in the languages it was translated into.
 *
 * A component is authored in one language, and every other language it is read
 * in is an entry beside that text: the language, the field of the component's
 * own props, and what to read there instead. A field is a path, so text inside
 * a list — the header of a table column, the label of a select option — is
 * named by the index it sits at (`columns.0.header`). Text a language has no
 * entry for is read as it was written.
 */
export interface UiTranslationEntry {
  locale: string;
  field: string;
  value: string;
}

const TRANSLATIONS_FIELD = "translations";

/** The translations a props record carries, in the order they were written. */
export function readUiTranslations(
  props: Record<string, unknown> | undefined,
): UiTranslationEntry[] {
  const entries = props?.[TRANSLATIONS_FIELD];
  if (!Array.isArray(entries)) return [];

  return entries.flatMap((entry) => {
    if (!isRecord(entry)) return [];

    const locale = readText(entry.locale);
    const field = readText(entry.field);
    const value = entry.value;
    if (!locale || !field || typeof value !== "string") return [];

    return [{ locale, field, value }];
  });
}

/**
 * The props of a component as this language reads them.
 *
 * Only the fields a translation names are replaced, and only the containers on
 * the way to them are copied, so an untranslated component is handed back
 * exactly as it came.
 */
export function translateUiProps<T extends Record<string, unknown>>(
  props: T,
  language: string | undefined,
): T {
  const entries = readUiTranslations(props)
    .filter((entry) => matchesLanguage(entry.locale, language));
  if (!entries.length) return props;

  return entries.reduce(
    (current, entry) => setAtPath(current, entry.field.split("."), entry.value),
    props,
  );
}

/** One translated field of a props record, or the text it was written with. */
export function translateUiText(
  props: Record<string, unknown> | undefined,
  field: string,
  language: string | undefined,
): string | null {
  const translated = readUiTranslations(props).find((entry) => (
    entry.field === field && matchesLanguage(entry.locale, language)
  ));
  if (translated) return translated.value;

  return readText(props?.[field]);
}

/**
 * A field of bound data as this language reads it.
 *
 * A record served by an API can carry translations of its own text the way a
 * component does — a tariff translates its `name` and `description` — so a
 * binding that lands on a field of such a record reads the translation beside
 * it. Anything else, and a language without an entry, reads the value itself.
 */
export function translateBoundValue(
  record: unknown,
  field: string | undefined,
  value: unknown,
  language: string | undefined,
): unknown {
  if (field === undefined || !isRecord(record)) return value;

  const translated = readUiTranslations(record).find((entry) => (
    entry.field === field && matchesLanguage(entry.locale, language)
  ));
  return translated ? translated.value : value;
}

// A locale answers for the language it names: the `en` of a page is what an
// `en-US` visitor reads, and the region a browser adds never hides it.
function matchesLanguage(locale: string, language: string | undefined): boolean {
  const spoken = readBaseLanguage(language);

  return Boolean(spoken) && readBaseLanguage(locale) === spoken;
}

function readBaseLanguage(value: string | undefined): string {
  return (value ?? "").trim().toLowerCase().split(/[-_]/)[0] ?? "";
}

function setAtPath<T>(container: T, path: readonly string[], value: string): T {
  const [key, ...rest] = path;
  if (key === undefined) return container;

  if (Array.isArray(container)) {
    const index = Number(key);
    if (!Number.isInteger(index) || index < 0 || index >= container.length) {
      return container;
    }

    const next = [...container];
    next[index] = rest.length
      ? setAtPath(container[index], rest, value)
      : value;
    return next as T;
  }

  if (!isRecord(container) || !(key in container)) return container;

  return {
    ...container,
    [key]: rest.length ? setAtPath(container[key], rest, value) : value,
  } as T;
}

function readText(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
